import { getNodesBounds, type Node, type ReactFlowInstance } from '@xyflow/react';

const ZOOM = 0.9;
const PADDING = 60;

interface PlaceFlowOptions {
  viewportWidth: number;
  viewportHeight: number;
  duration?: number;
  /** Called when bbox is empty / viewport not measured yet. */
  fallback?: () => void;
}

/**
 * Position the flow canvas with a locked zoom level, pinned to the leading
 * edge of the viewport (top for TB, left for LR), cross-axis centered.
 *
 * Why this exists, in three lines:
 * - Zoom is locked at 0.9 so card density stays constant across flow sizes —
 *   no scaling for "fit", no zoom-out for dense flows, no zoom-in for sparse.
 * - Position pins the bbox to the leading edge (top/left), not centered.
 *   Centering wastes upper/left space on short flows and chops the top/left
 *   of tall/wide flows that exceed the viewport.
 * - The bbox is computed from content nodes only — container nodes
 *   (`flow`, `secondary_flow`) size to worst-case branching width which
 *   would skew the centering of the cross-axis.
 *
 * Single caller surface used by both the layout-toggle handler and the
 * initial-paint hook so the two entry points behave identically.
 */
export function placeFlowInViewport(
  flowInstance: ReactFlowInstance,
  nodes: Node[],
  isTB: boolean,
  options: PlaceFlowOptions,
): void {
  const { viewportWidth, viewportHeight, duration, fallback } = options;

  const contentNodes = nodes.filter((n) => n.type !== 'flow' && n.type !== 'secondary_flow');
  if (contentNodes.length === 0 || !viewportWidth || !viewportHeight) {
    fallback?.();
    return;
  }

  const bbox = getNodesBounds(contentNodes);
  // getNodesBounds returns {x, y, width, height} in flow coordinates.

  const x = isTB ? viewportWidth / 2 - (bbox.x + bbox.width / 2) * ZOOM : PADDING - bbox.x * ZOOM;
  const y = isTB ? PADDING - bbox.y * ZOOM : viewportHeight / 2 - (bbox.y + bbox.height / 2) * ZOOM;

  flowInstance.setViewport({ x, y, zoom: ZOOM }, duration ? { duration } : undefined);
}
